import { t } from "@lingui/core/macro"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { Link, Outlet, createFileRoute, useLocation, useSearch } from "@tanstack/react-router"
import {
  ArrowUpDown,
  Globe,
  Grid3X3,
  Home,
  Languages,
  Layers,
  List,
  Package,
  Ruler,
  Settings2,
  XCircle,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { LIST_OF_MATERIALS_IN_NAVIGATION } from "@/features/list-of-materials/navigation"
import {
  getConfigurationVersionFn,
  listConfigurationVersionsFn,
} from "@/features/store-configuration/server/storeConfiguration"
import { storeConfigurationQueryKeys } from "@/lib/queryKeys"
import { cn } from "@/lib/utils"

const configurationVersionQuery = (versionId: string) =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.json(versionId),
    queryFn: () => getConfigurationVersionFn({ data: { id: versionId } }),
  })

const configurationVersionsListQuery = () =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.list({ includeArchived: false }),
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

export const Route = createFileRoute("/(authenticated)/settings/configuration-versions/$versionId")(
  {
    loader: ({ context, params }) => {
      void context.queryClient.ensureQueryData(configurationVersionQuery(params.versionId))
      void context.queryClient.ensureQueryData(configurationVersionsListQuery())
    },
    component: ConfigurationVersionLayout,
  },
)

type NavItem = {
  label: string
  icon: React.ComponentType<{ className?: string }>
} & ({ href: string; search?: undefined } | { href: string; search: Record<string, string> })

type NavHeading = { heading: string }

function getNavItems(versionId: string): (NavItem | NavHeading)[] {
  const base = `/settings/configuration-versions/${versionId}`
  return [
    {
      label: t`Overview`,
      href: base,
      icon: Home,
    },
    //headings groups nav items for clear navigation
    { heading: t`General` },
    {
      label: t`Global`,
      href: `${base}/edit`,
      search: { section: "global-constraints" },
      icon: Settings2,
    },
    {
      label: t`Translations`,
      href: `${base}/translations`,
      icon: Languages,
    },
    { heading: t`Categories & Bandwidths` },
    {
      label: t`Categories`,
      href: `${base}/edit`,
      search: { section: "categories" },
      icon: Layers,
    },
    {
      label: t`Bandwidths`,
      href: `${base}/edit`,
      search: { section: "bandwidths" },
      icon: Ruler,
    },
    {
      label: t`Category Order`,
      href: `${base}/edit`,
      search: { section: "category-sort-order" },
      icon: ArrowUpDown,
    },
    {
      label: t`Country specific bandwidths`,
      href: `${base}/edit`,
      search: { section: "country-hints" },
      icon: Globe,
    },
    { heading: t`Layout Rules` },
    {
      label: t`Wall Order`,
      href: `${base}/edit`,
      search: { section: "wall-order" },
      icon: List,
    },
    // Placement Sequence (aisle rules) — hidden; superseded by Layout Rules.
    // {
    //   label: t`Placement Sequence`,
    //   href: `${base}/edit`,
    //   search: { section: "placement-sequence" },
    //   icon: List,
    // },
    {
      label: t`Layout Rules`,
      href: `${base}/edit`,
      search: { section: "layout-rules" },
      icon: Settings2,
    },
    // Abstract Rules - hidden for now, replaced by Placement Sequence
    // {
    //   label: t`Abstract Rules`,
    //   href: `${base}/edit`,
    //   search: { section: "abstract-rules" },
    //   icon: List,
    // },
    {
      label: t`Prohibited`,
      href: `${base}/edit`,
      search: { section: "prohibited-rules" },
      icon: XCircle,
    },
    {
      label: t`Gondola Arrangements`,
      href: `${base}/edit`,
      search: { section: "gondola-arrangements" },
      icon: Package,
    },
    // Placement Config - hidden, gondolaPlacementConfig is not used by the
    // current placement algorithm (allocateGondolasV2 ignores it).
    // {
    //   label: t`Placement Config`,
    //   href: `${base}/edit`,
    //   search: { section: "gondola-placement" },
    //   icon: Sliders,
    // },
    ...(LIST_OF_MATERIALS_IN_NAVIGATION
      ? [
          { heading: t`List of Materials` },
          {
            label: t`List of Materials`,
            href: "/list-of-materials",
            search: { version: versionId },
            icon: Grid3X3,
          },
        ]
      : []),
  ]
}

function isNavItemActive(
  item: NavItem,
  pathname: string,
  overviewHref: string,
  currentSection: string | undefined,
): boolean {
  if (item.search) return pathname.endsWith("/edit") && currentSection === item.search.section
  if (item.href === overviewHref) return pathname === overviewHref
  return pathname.startsWith(item.href)
}

/** The active nav item plus the heading it sits under, for the breadcrumb. */
function findActiveTrail(
  navItems: (NavItem | NavHeading)[],
  isActive: (item: NavItem) => boolean,
): { heading: string | null; item: NavItem } | null {
  let heading: string | null = null
  for (const item of navItems) {
    if ("heading" in item) {
      heading = item.heading
    } else if (isActive(item)) {
      return { heading, item }
    }
  }
  return null
}

function ConfigurationVersionLayout() {
  const { versionId } = Route.useParams()
  const location = useLocation()
  const { data: config } = useSuspenseQuery(configurationVersionQuery(versionId))
  const { data: versions } = useSuspenseQuery(configurationVersionsListQuery())

  const navItems = getNavItems(versionId)
  const currentVersion = versions.find((v) => v.id === versionId)
  const isActive = currentVersion?.isActive ?? false
  const versionName = config?.storeConfiguration.version ?? "..."

  // Get current search params for active state detection on edit sections
  const search = useSearch({ strict: false }) as { section?: string }
  const currentSection = search?.section

  const overviewHref = `/settings/configuration-versions/${versionId}`
  const isItemActive = (item: NavItem) =>
    isNavItemActive(item, location.pathname, overviewHref, currentSection)

  /**  Meant to show the label which groups the nav item in the breadcrumb as well
   *    e.g shelf codes sits underneath the LoM  so breadcrumbs shows Overview > LoM > Shelf Codes
   *    The Heading has no page so it's not clickable meaning only overview is
   */
  const activeTrail = findActiveTrail(navItems, isItemActive)
  const showBreadcrumb = activeTrail !== null && activeTrail.item.href !== overviewHref

  return (
    <div className="flex h-full flex-col">
      {/* Compact header with version name and status */}
      <div className="flex shrink-0 flex-col gap-2 pb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-2xl font-bold text-neutral-900 dark:text-white">
            {versionName}
          </h1>
          {isActive ? (
            <Badge variant="success" className="text-xs">
              {t`Active`}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              {t`Draft`}
            </Badge>
          )}
        </div>
        {showBreadcrumb && (
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link className="font-semibold" to={overviewHref}>{t`Overview`}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {activeTrail.heading && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>{activeTrail.heading}</BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-bold">{activeTrail.item.label}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-4 pb-4">
        <div className="z-30 w-12 shrink-0">
          <nav
            className="group/nav flex max-h-full min-h-0 w-12 flex-col gap-1 overflow-y-auto overflow-x-hidden rounded-lg border border-neutral-200 bg-white p-1 shadow-sm transition-[width] duration-150 ease-out [interpolate-size:allow-keywords] hover:w-fit has-[:focus-visible]:w-fit dark:border-neutral-700 dark:bg-neutral-950"
            aria-label={t`Configuration version sections`}
          >
            {navItems.map((item) => {
              if ("heading" in item) {
                return (
                  <div
                    key={`heading-${item.heading}`}
                    role="presentation"
                    className="relative my-2 border-t border-neutral-200 dark:border-neutral-700"
                  >
                    {/* The label is centred on the divider line, so it costs no height in the collapsed version. */}
                    <span className="absolute left-3 top-0 -translate-y-1/2 whitespace-nowrap bg-white px-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 opacity-0 transition-opacity duration-150 group-hover/nav:opacity-100 group-has-[:focus-visible]/nav:opacity-100 dark:bg-neutral-950 dark:text-neutral-400">
                      {item.heading}
                    </span>
                  </div>
                )
              }

              const active = isItemActive(item)

              return (
                <Link
                  key={item.search ? `edit-${item.search.section}` : item.href}
                  to={item.href}
                  search={item.search}
                  aria-label={item.label}
                  title={item.label}
                  className={cn(
                    "flex items-center gap-2 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors",
                    active
                      ? "border-neutral-900 bg-neutral-100 text-neutral-900 dark:border-white dark:bg-neutral-800 dark:text-white"
                      : "border-transparent text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800/50 dark:hover:text-neutral-200",
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span
                    aria-hidden="true"
                    className="whitespace-nowrap opacity-0 transition-opacity duration-150 group-hover/nav:opacity-100 group-has-[:focus-visible]/nav:opacity-100"
                  >
                    {item.label}
                  </span>
                </Link>
              )
            })}
          </nav>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
